/**
 * SavedEstimates Component
 * Allows users to save, load, and manage their estimates
 * Supports both cloud storage (MongoDB) and local device storage
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Save, FolderOpen, Trash2, Copy, Cloud, HardDrive, Clock,
  AlertCircle, CheckCircle, RefreshCw, FileText, Download,
  Upload, ChevronDown, ChevronUp, Search, Plus, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Format currency
const formatCurrency = (num) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num || 0);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SavedEstimates = ({ 
  getEstimateData, 
  loadEstimateData,
  currentProjectName = '' 
}) => {
  const { user, getSessionToken } = useAuth();
  
  // Get session token - always get fresh from localStorage
  const getToken = useCallback(() => {
    return getSessionToken ? getSessionToken() : localStorage.getItem('session_token');
  }, [getSessionToken]);
  
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [estimateName, setEstimateName] = useState('');
  const [estimateDescription, setEstimateDescription] = useState('');
  const [cloudEstimates, setCloudEstimates] = useState([]);
  const [localEstimates, setLocalEstimates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('cloud');
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Load cloud estimates from backend
  const loadCloudEstimates = useCallback(async () => {
    const token = getToken();
    if (!user || !token) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/estimates/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCloudEstimates(data.estimates || []);
      }
    } catch (error) {
      console.error('Failed to load cloud estimates:', error);
    } finally {
      setLoading(false);
    }
  }, [user, getToken]);

  // Load local estimates from localStorage
  const loadLocalEstimates = useCallback(() => {
    try {
      const saved = localStorage.getItem('massMasterPro_savedEstimates');
      if (saved) {
        setLocalEstimates(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load local estimates:', error);
    }
  }, []);

  // Load estimates on mount
  useEffect(() => {
    loadCloudEstimates();
    loadLocalEstimates();
  }, [loadCloudEstimates, loadLocalEstimates]);

  // Save estimate to cloud (MongoDB)
  const saveToCloud = async () => {
    if (!estimateName.trim()) {
      toast.error('Please enter an estimate name');
      return;
    }
    
    const token = getToken();
    if (!user || !token) {
      toast.error('Please log in to save to cloud');
      return;
    }
    
    try {
      setLoading(true);
      const estimateData = getEstimateData();
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/estimates/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: estimateName.trim(),
          description: estimateDescription.trim(),
          estimate_data: estimateData
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowSaveDialog(false);
        setEstimateName('');
        setEstimateDescription('');
        loadCloudEstimates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save estimate');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save estimate');
    } finally {
      setLoading(false);
    }
  };

  // Save estimate to local device
  const saveToLocal = () => {
    if (!estimateName.trim()) {
      toast.error('Please enter an estimate name');
      return;
    }
    
    try {
      const estimateData = getEstimateData();
      const now = new Date().toISOString();
      
      const newEstimate = {
        estimate_id: `local_${Date.now()}`,
        name: estimateName.trim(),
        description: estimateDescription.trim(),
        grand_total: estimateData.grandTotals?.grandTotal || 0,
        estimate_data: estimateData,
        created_at: now,
        updated_at: now
      };
      
      // Check if estimate with same name exists
      const existingIndex = localEstimates.findIndex(e => e.name === newEstimate.name);
      let updatedEstimates;
      
      if (existingIndex >= 0) {
        updatedEstimates = [...localEstimates];
        updatedEstimates[existingIndex] = {
          ...newEstimate,
          estimate_id: localEstimates[existingIndex].estimate_id,
          created_at: localEstimates[existingIndex].created_at
        };
        toast.success(`Estimate "${newEstimate.name}" updated on device`);
      } else {
        updatedEstimates = [newEstimate, ...localEstimates];
        toast.success(`Estimate "${newEstimate.name}" saved to device`);
      }
      
      // Keep only last 50 local estimates
      updatedEstimates = updatedEstimates.slice(0, 50);
      
      localStorage.setItem('massMasterPro_savedEstimates', JSON.stringify(updatedEstimates));
      setLocalEstimates(updatedEstimates);
      setShowSaveDialog(false);
      setEstimateName('');
      setEstimateDescription('');
    } catch (error) {
      console.error('Local save error:', error);
      toast.error('Failed to save estimate to device');
    }
  };

  // Load estimate from cloud
  const loadFromCloud = async (estimateId) => {
    const token = getToken();
    if (!user || !token) {
      toast.error('Please log in to load cloud estimates');
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/estimates/${estimateId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        loadEstimateData(data.estimate.estimate_data);
        toast.success(`Loaded "${data.estimate.name}"`);
        setShowLoadDialog(false);
      } else {
        toast.error('Failed to load estimate');
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load estimate');
    } finally {
      setLoading(false);
    }
  };

  // Load estimate from local storage
  const loadFromLocal = (estimate) => {
    try {
      loadEstimateData(estimate.estimate_data);
      toast.success(`Loaded "${estimate.name}" from device`);
      setShowLoadDialog(false);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load estimate');
    }
  };

  // Delete estimate from cloud
  const deleteFromCloud = async (estimateId) => {
    const token = getToken();
    if (!user || !token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/estimates/${estimateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success('Estimate deleted');
        loadCloudEstimates();
      } else {
        toast.error('Failed to delete estimate');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete estimate');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  // Delete estimate from local storage
  const deleteFromLocal = (estimateId) => {
    try {
      const updatedEstimates = localEstimates.filter(e => e.estimate_id !== estimateId);
      localStorage.setItem('massMasterPro_savedEstimates', JSON.stringify(updatedEstimates));
      setLocalEstimates(updatedEstimates);
      toast.success('Estimate deleted from device');
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete estimate');
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (estimate, isCloud) => {
    setDeleteTarget({ estimate, isCloud });
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const confirmDelete = () => {
    if (!deleteTarget) return;
    
    if (deleteTarget.isCloud) {
      deleteFromCloud(deleteTarget.estimate.estimate_id);
    } else {
      deleteFromLocal(deleteTarget.estimate.estimate_id);
    }
  };

  // Duplicate cloud estimate
  const duplicateEstimate = async (estimateId) => {
    const token = getToken();
    if (!user || !token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/estimates/duplicate/${estimateId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Created copy: "${data.name}"`);
        loadCloudEstimates();
      } else {
        toast.error('Failed to duplicate estimate');
      }
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate estimate');
    } finally {
      setLoading(false);
    }
  };

  // Export estimate as JSON file
  const exportEstimate = (estimate) => {
    try {
      const fileName = `${estimate.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.json`;
      const blob = new Blob([JSON.stringify(estimate.estimate_data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Estimate exported');
    } catch (error) {
      toast.error('Failed to export estimate');
    }
  };

  // Filter estimates by search query
  const filteredCloudEstimates = cloudEstimates.filter(e => 
    e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (e.description && e.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  const filteredLocalEstimates = localEstimates.filter(e => 
    e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (e.description && e.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Estimate card component
  const EstimateCard = ({ estimate, isCloud, onLoad, onDelete, onDuplicate, onExport }) => (
    <Card className="hover:bg-muted/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {isCloud ? (
                <Cloud className="w-4 h-4 text-blue-500 shrink-0" />
              ) : (
                <HardDrive className="w-4 h-4 text-green-500 shrink-0" />
              )}
              <h4 className="font-semibold text-foreground truncate">{estimate.name}</h4>
            </div>
            {estimate.description && (
              <p className="text-sm text-muted-foreground line-clamp-1 mb-2">{estimate.description}</p>
            )}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="font-medium text-primary">{formatCurrency(estimate.grand_total)}</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(estimate.updated_at)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => onLoad(estimate)}
              className="h-8 px-2"
              data-testid={`load-estimate-${estimate.estimate_id}`}
            >
              <FolderOpen className="w-4 h-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 px-2">
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onLoad(estimate)}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Load
                </DropdownMenuItem>
                {isCloud && onDuplicate && (
                  <DropdownMenuItem onClick={() => onDuplicate(estimate.estimate_id)}>
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onExport(estimate)}>
                  <Download className="w-4 h-4 mr-2" />
                  Export JSON
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => onDelete(estimate)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      {/* Save/Load Buttons */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          onClick={() => {
            setEstimateName(currentProjectName);
            setShowSaveDialog(true);
          }}
          data-testid="save-estimate-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          Save
        </Button>
        <Button 
          variant="outline" 
          onClick={() => {
            loadCloudEstimates();
            loadLocalEstimates();
            setShowLoadDialog(true);
          }}
          data-testid="load-estimate-btn"
        >
          <FolderOpen className="w-4 h-4 mr-2" />
          Load
        </Button>
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Save Estimate
            </DialogTitle>
            <DialogDescription>
              Save your current estimate to access it later
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Estimate Name *</Label>
              <Input
                value={estimateName}
                onChange={(e) => setEstimateName(e.target.value)}
                placeholder="e.g., Commercial Site - Phase 1"
                data-testid="estimate-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={estimateDescription}
                onChange={(e) => setEstimateDescription(e.target.value)}
                placeholder="Brief description of the project"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={saveToLocal}
              disabled={loading || !estimateName.trim()}
              className="w-full sm:w-auto"
              data-testid="save-to-device-btn"
            >
              <HardDrive className="w-4 h-4 mr-2" />
              Save to Device
            </Button>
            <Button 
              onClick={saveToCloud}
              disabled={loading || !estimateName.trim() || !user}
              className="w-full sm:w-auto"
              data-testid="save-to-cloud-btn"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Cloud className="w-4 h-4 mr-2" />
              )}
              Save to Cloud
            </Button>
          </DialogFooter>
          {!user && (
            <p className="text-xs text-muted-foreground text-center">
              Log in to save estimates to the cloud for access on any device
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Load Dialog */}
      <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              Load Estimate
            </DialogTitle>
            <DialogDescription>
              Load a previously saved estimate
            </DialogDescription>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search estimates..."
              className="pl-9"
            />
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="cloud" className="flex-1 gap-2">
                <Cloud className="w-4 h-4" />
                Cloud ({filteredCloudEstimates.length})
              </TabsTrigger>
              <TabsTrigger value="device" className="flex-1 gap-2">
                <HardDrive className="w-4 h-4" />
                Device ({filteredLocalEstimates.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="cloud" className="mt-4">
              <ScrollArea className="h-[400px] pr-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredCloudEstimates.length === 0 ? (
                  <div className="text-center py-8">
                    <Cloud className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                    <p className="text-muted-foreground">
                      {user ? 'No cloud estimates found' : 'Log in to access cloud estimates'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCloudEstimates.map((estimate) => (
                      <EstimateCard
                        key={estimate.estimate_id}
                        estimate={estimate}
                        isCloud={true}
                        onLoad={() => loadFromCloud(estimate.estimate_id)}
                        onDelete={() => handleDeleteClick(estimate, true)}
                        onDuplicate={duplicateEstimate}
                        onExport={exportEstimate}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="device" className="mt-4">
              <ScrollArea className="h-[400px] pr-4">
                {filteredLocalEstimates.length === 0 ? (
                  <div className="text-center py-8">
                    <HardDrive className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                    <p className="text-muted-foreground">No estimates saved on this device</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredLocalEstimates.map((estimate) => (
                      <EstimateCard
                        key={estimate.estimate_id}
                        estimate={estimate}
                        isCloud={false}
                        onLoad={() => loadFromLocal(estimate)}
                        onDelete={() => handleDeleteClick(estimate, false)}
                        onExport={exportEstimate}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
          
          <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-3">
            <AlertCircle className="w-4 h-4" />
            <span>Device storage is browser-specific. Cloud storage syncs across all devices.</span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Estimate?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.estimate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SavedEstimates;
